import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar } from "lucide-react";
import { useBlogs } from "@/hooks/usePortfolio";
import { getImageUrl } from "@/api/client";
import { SectionTitle } from "@/components/SectionTitle";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

export const Blogs = () => {
  const { data: blogs, isLoading, error } = useBlogs();

  if (isLoading || error || !blogs) return null;

  return (
    <section id="blogs" className="section-pad relative" data-testid="blogs-section">
      <div className="container-x">
        <SectionTitle eyebrow="Journal" title={<>Notes on <span className="italic gold-text">business & sales</span></>} subtitle="Long-form reflections on selling, leadership and growth — drawn from two decades on the field." />
        <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6">
          {blogs.map((b, i) => (
            <motion.article
              key={b.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              data-testid={`blog-card-${b.id}`}
              className="group glass rounded-2xl overflow-hidden grain flex flex-col"
            >
              <Link to={`/blog/${b.slug}`} className="block">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={getImageUrl(b.featuredImageUrl)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1200" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))]/60 via-transparent to-transparent" />
                </div>
              </Link>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  {fmt(b.publishedDate)}
                </div>
                <Link to={`/blog/${b.slug}`}>
                  <h3 className="mt-3 font-serif text-xl leading-snug hover:text-[hsl(var(--gold-deep))] dark:hover:text-[hsl(var(--gold-light))] transition-colors">{b.title}</h3>
                </Link>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-3">{b.summary}</p>
                <Link
                  to={`/blog/${b.slug}`}
                  data-testid={`blog-read-${b.id}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] group-hover:gap-3 transition-all"
                >
                  Read Article <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
